import * as React from 'react';
import { Accordion as AccordionPrimitive } from 'radix-ui';
import { CaretDown, CaretRight } from '@phosphor-icons/react';

import { cn } from '@/lib/utils';

const SINGLE_ITEM_VALUE = 'item';
type AccordionContextVariant = 'default' | 'modal';

const AccordionContext =
  React.createContext<AccordionContextVariant>('default');

type AccordionClasses = {
  root?: string;
};

interface AccordionProps {
  children: React.ReactNode;
  className?: string;
  id?: string;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onChange?: (event: React.SyntheticEvent, expanded: boolean) => void;
  sx?: React.CSSProperties;
  classes?: AccordionClasses;
  context?: AccordionContextVariant;
}

const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      className,
      children,
      id,
      defaultExpanded = false,
      expanded,
      onChange,
      sx,
      classes,
      context = 'default',
    },
    ref
  ) => {
    const isControlled = expanded !== undefined;

    const [internalExpanded, setInternalExpanded] =
      React.useState(defaultExpanded);

    const isExpanded = isControlled ? Boolean(expanded) : internalExpanded;

    const handleExpandedChange = React.useCallback(
      (nextExpanded: boolean) => {
        if (!isControlled) {
          setInternalExpanded(nextExpanded);
        }
        onChange?.({} as React.SyntheticEvent, nextExpanded);
      },
      [isControlled, onChange]
    );

    return (
      <AccordionContext.Provider value={context}>
        <AccordionPrimitive.Root
          ref={ref}
          type="single"
          collapsible
          value={isExpanded ? SINGLE_ITEM_VALUE : ''}
          onValueChange={(value) => {
            handleExpandedChange(value === SINGLE_ITEM_VALUE);
          }}
          data-state={isExpanded ? 'open' : 'closed'}
          data-slot="accordion"
          data-context={context}
          className={cn('ui-accordion w-full', classes?.root, className)}
          style={sx}
          id={id}
        >
          <AccordionPrimitive.Item
            value={SINGLE_ITEM_VALUE}
            data-slot="accordion-item"
            className="ui-accordion-item"
          >
            {children}
          </AccordionPrimitive.Item>
        </AccordionPrimitive.Root>
      </AccordionContext.Provider>
    );
  }
);

Accordion.displayName = 'Accordion';

interface AccordionSummaryProps extends React.ComponentPropsWithoutRef<'button'> {
  sx?: React.CSSProperties;
  layout?: 'auto' | 'start' | 'between';
  indicator?: boolean;
}

const AccordionSummary = React.forwardRef<
  HTMLButtonElement,
  AccordionSummaryProps
>(
  (
    { className, children, sx, layout = 'auto', indicator = true, ...props },
    ref
  ) => {
    const accordionContext = React.useContext(AccordionContext);
    const isModalContext = accordionContext === 'modal';
    const visibleChildren = React.Children.toArray(children);
    const visibleChildrenCount = visibleChildren.length;

    const resolvedLayout =
      layout === 'auto'
        ? visibleChildrenCount === 2
          ? 'between'
          : 'start'
        : layout;

    const indicatorNode = indicator ? (
      <>
        <CaretRight
          aria-hidden="true"
          focusable="false"
          weight="fill"
          className={cn(
            'm-0 size-3 block group-data-[state=open]:hidden',
            isModalContext
              ? 'text-[var(--color-neutral-25)]'
              : 'text-fg-primary'
          )}
        />
        <CaretDown
          aria-hidden="true"
          focusable="false"
          weight="fill"
          className={cn(
            'm-0 size-3 hidden group-data-[state=open]:block',
            isModalContext
              ? 'text-[var(--color-neutral-25)]'
              : 'text-fg-primary'
          )}
        />
      </>
    ) : null;

    // display: contents (inline, so it outranks the global unlayered h3 SCSS)
    // keeps the h3 in the accessibility tree without generating a box: call
    // sites pass layout classes like flex-1 through to the trigger, so the
    // trigger must remain a direct flex item of the surrounding row.
    return (
      <AccordionPrimitive.Header style={{ display: 'contents' }}>
        <AccordionPrimitive.Trigger
          ref={ref}
          data-slot="accordion-summary"
          className={cn(
            'group accordion-summary m-0 flex w-full items-center border-0 bg-transparent p-0 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring-default)]',
            isModalContext && 'text-[var(--color-neutral-25)]',
            resolvedLayout === 'between' ? 'justify-between' : 'justify-start',
            className
          )}
          style={sx}
          {...props}
        >
          {resolvedLayout === 'between' && visibleChildrenCount === 2 ? (
            <>
              <div className="flex min-w-0 items-center gap-2 w-full">
                {indicatorNode}
                {visibleChildren[0]}
              </div>
              {visibleChildren[1]}
            </>
          ) : (
            <div className="flex min-w-0 items-center gap-2 w-full">
              {indicatorNode}
              {children}
            </div>
          )}
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
    );
  }
);

AccordionSummary.displayName = 'AccordionSummary';

interface AccordionDetailsProps extends React.ComponentPropsWithoutRef<'div'> {
  sx?: React.CSSProperties;
}

const AccordionDetails = React.forwardRef<
  HTMLDivElement,
  AccordionDetailsProps
>(({ className, children, sx, ...props }, ref) => {
  const accordionContext = React.useContext(AccordionContext);
  const isModalContext = accordionContext === 'modal';

  return (
    <AccordionPrimitive.Content
      data-slot="accordion-collapse"
      className="ui-accordion-collapse overflow-hidden"
    >
      <div
        ref={ref}
        data-slot="accordion-details"
        className={cn(
          'ui-accordion-details pr-0',
          isModalContext && 'text-[var(--color-neutral-25)]',
          className
        )}
        style={sx}
        {...props}
      >
        {children}
      </div>
    </AccordionPrimitive.Content>
  );
});

AccordionDetails.displayName = 'AccordionDetails';

export { Accordion, AccordionSummary, AccordionDetails };
